import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useGameScrollLock } from './useGameScrollLock';
import { useModalHistory } from './useModalHistory';

export function useMaximizedMap(gameId: string) {
  const mapRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const [expanded, setExpanded] = useState<{ gameId: string; height: number } | null>(null);
  const maximized = expanded?.gameId === gameId;
  const wasMaximized = useRef(false);
  useGameScrollLock(maximized);
  useModalHistory(maximized, () => setExpanded(null));

  // Run after inspector cleanup, which can also restore its previous focus.
  useEffect(() => {
    if (wasMaximized.current && !maximized) toggleRef.current?.focus({ preventScroll: true });
    wasMaximized.current = maximized;
  }, [maximized]);

  useLayoutEffect(() => { setExpanded(null); }, [gameId]);
  useLayoutEffect(() => {
    if (!maximized || !mapRef.current) return;
    const map = mapRef.current;
    const toggle = toggleRef.current;
    // The frame creates a stacking context; raise it above the dock/action sheet.
    const frame = map.closest('.game-map-frame');
    frame?.classList.add('game-map-frame-maximized');
    const background: { element: Element; inert: string | null }[] = [];
    for (let branch: Element = map; branch.parentElement; branch = branch.parentElement) {
      for (const sibling of Array.from(branch.parentElement.children)) {
        if (sibling === branch || sibling.matches('.game-dialog-backdrop, script, style')) continue;
        background.push({ element: sibling, inert: sibling.getAttribute('inert') });
        sibling.setAttribute('inert', '');
      }
    }
    const hasInspector = () => !!document.querySelector('.game-detail-dialog[aria-modal="true"], .gate-tooltip');
    const focusMap = () => toggle?.focus({ preventScroll: true });
    const keyDown = (event: KeyboardEvent) => {
      if (hasInspector() || event.defaultPrevented) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        setExpanded(null);
      } else if (event.key === 'Tab') {
        const controls = Array.from(map.querySelectorAll<HTMLElement>(
          'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), [tabindex="0"], summary'
        )).filter(element => !element.closest('[hidden], [inert]') && getComputedStyle(element).display !== 'none'
          && getComputedStyle(element).visibility !== 'hidden' && element.getClientRects().length > 0);
        const first = controls[0], last = controls[controls.length - 1];
        if (event.shiftKey && (document.activeElement === first || !map.contains(document.activeElement))) {
          event.preventDefault(); (last ?? toggle)?.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !map.contains(document.activeElement))) {
          event.preventDefault(); (first ?? toggle)?.focus();
        }
      }
    };
    const focusIn = (event: FocusEvent) => {
      if (!hasInspector() && !map.contains(event.target as Node)) focusMap();
    };
    focusMap();
    document.addEventListener('keydown', keyDown, true);
    document.addEventListener('focusin', focusIn);
    return () => {
      document.removeEventListener('keydown', keyDown, true);
      document.removeEventListener('focusin', focusIn);
      frame?.classList.remove('game-map-frame-maximized');
      background.forEach(({ element, inert }) => {
        if (inert === null) element.removeAttribute('inert'); else element.setAttribute('inert', inert);
      });
      if (toggle?.isConnected) toggle.focus({ preventScroll: true });
    };
  }, [maximized]);

  return { mapRef, toggleRef, maximized, placeholderHeight: maximized ? expanded.height : undefined,
    toggleMaximized: () => setExpanded(maximized ? null : { gameId, height: mapRef.current?.getBoundingClientRect().height ?? 0 }) };
}
